import { PermissionCode, type ManagedTeam } from '@gb-ticket/shared-types';
import { Loader2Icon, MoreHorizontalIcon, PlusIcon } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { hasPermission } from '@/features/auth/permissions';
import { useMe } from '@/features/auth/use-me';
import { PlatformPageHeader } from '@/platform/components/platform-page-header';
import { adminPanelClassName, adminTableFrameClassName } from '@/platform/features/admin-panel';
import {
  TeamFormDialog,
  type TeamFormValues,
} from '@/platform/features/organisation/team-form-dialog';
import { useDialogInvalidationHold } from '@/platform/lib/use-invalidation-hold';
import { TeamMembersDialog } from '@/platform/features/organisation/team-members-dialog';
import {
  useCreateTeam,
  useDeleteTeam,
  useDepartments,
  useTeams,
  useUpdateTeam,
} from '@/platform/features/organisation/use-organisation';
import { useStaffUsers } from '@/platform/features/users/use-users';
import { ApiRequestError } from '@/lib/api';
import { cn } from '@/lib/utils';

type DialogState =
  { mode: 'create' } | { mode: 'edit'; values: TeamFormValues; leadLabel?: string } | null;

type DeactivateState = {
  id: string;
  name: string;
  userCount: number;
  queueCount: number;
} | null;

type DeleteState = {
  id: string;
  name: string;
} | null;

type MembersState = { id: string; name: string } | null;

function toFormValues(item: ManagedTeam): TeamFormValues {
  return {
    id: item.id,
    name: item.name,
    slug: item.slug,
    departmentId: item.departmentId,
    leadId: item.leadId,
    isActive: item.isActive,
  };
}

export function TeamsPage() {
  const me = useMe();
  const canWrite = hasPermission(me.data, PermissionCode.USERS_MANAGE);
  const [searchInput, setSearchInput] = useState('');
  const [q, setQ] = useState<string | undefined>();
  const teams = useTeams({ q, includeInactive: true });
  const departments = useDepartments({ includeInactive: true });
  const staff = useStaffUsers();
  const createTeam = useCreateTeam();
  const updateTeam = useUpdateTeam();
  const deleteTeam = useDeleteTeam();
  const [dialog, setDialog] = useState<DialogState>(null);
  const [deactivate, setDeactivate] = useState<DeactivateState>(null);
  const [pendingDelete, setPendingDelete] = useState<DeleteState>(null);
  const [members, setMembers] = useState<MembersState>(null);
  useDialogInvalidationHold(
    dialog !== null || deactivate !== null || pendingDelete !== null || members !== null,
  );

  useEffect(() => {
    const handle = window.setTimeout(() => {
      setQ(searchInput.trim() || undefined);
    }, 300);
    return () => window.clearTimeout(handle);
  }, [searchInput]);

  const busy = createTeam.isPending || updateTeam.isPending;

  const leadOptions = useMemo(() => {
    const agents = staff.data?.items ?? [];
    if (dialog?.mode === 'edit' && dialog.values.leadId) {
      const exists = agents.some((agent) => agent.id === dialog.values.leadId);
      if (!exists) {
        return [
          {
            id: dialog.values.leadId,
            displayName: dialog.leadLabel ?? 'Aktuelle Leitung',
          },
          ...agents,
        ];
      }
    }
    return agents;
  }, [staff.data?.items, dialog]);

  async function handleFormSubmit(payload: {
    create?: Parameters<typeof createTeam.mutateAsync>[0];
    update?: Parameters<typeof updateTeam.mutateAsync>[0]['body'];
  }) {
    try {
      if (dialog?.mode === 'create' && payload.create) {
        await createTeam.mutateAsync(payload.create);
        toast.success('Team angelegt.');
        setDialog(null);
        return;
      }
      if (dialog?.mode === 'edit' && payload.update && dialog.values.id) {
        await updateTeam.mutateAsync({ id: dialog.values.id, body: payload.update });
        toast.success(
          payload.update.isActive === false ? 'Team deaktiviert.' : 'Team gespeichert.',
        );
        setDialog(null);
      }
    } catch (error) {
      toast.error(
        error instanceof ApiRequestError
          ? error.message
          : 'Das Team konnte nicht gespeichert werden.',
      );
    }
  }

  async function confirmDeactivate() {
    if (!deactivate) {
      return;
    }
    try {
      await updateTeam.mutateAsync({ id: deactivate.id, body: { isActive: false } });
      toast.success('Team deaktiviert.');
      setDeactivate(null);
    } catch (error) {
      toast.error(
        error instanceof ApiRequestError
          ? error.message
          : 'Das Team konnte nicht deaktiviert werden.',
      );
    }
  }

  async function reactivate(item: ManagedTeam) {
    try {
      await updateTeam.mutateAsync({ id: item.id, body: { isActive: true } });
      toast.success('Team reaktiviert.');
    } catch (error) {
      toast.error(
        error instanceof ApiRequestError
          ? error.message
          : 'Das Team konnte nicht reaktiviert werden.',
      );
    }
  }

  async function confirmDelete() {
    if (!pendingDelete) {
      return;
    }
    try {
      await deleteTeam.mutateAsync(pendingDelete.id);
      toast.success(`„${pendingDelete.name}“ wurde endgültig gelöscht.`);
      setPendingDelete(null);
    } catch (error) {
      toast.error(
        error instanceof ApiRequestError ? error.message : 'Das Team konnte nicht gelöscht werden.',
      );
    }
  }

  function usageHint(state: DeactivateState): string {
    if (!state) {
      return '';
    }
    const parts: string[] = [];
    if (state.userCount > 0) {
      parts.push(`${state.userCount} Mitglied${state.userCount === 1 ? '' : 'er'}`);
    }
    if (state.queueCount > 0) {
      parts.push(`${state.queueCount} Warteschlange${state.queueCount === 1 ? '' : 'n'}`);
    }
    if (parts.length === 0) {
      return `„${state.name}“ wird deaktiviert und erscheint nicht mehr in neuen Zuweisungen.`;
    }
    return `„${state.name}“ wird deaktiviert. Betroffen: ${parts.join(', ')}. Bestehende Zuordnungen bleiben erhalten.`;
  }

  return (
    <section className="space-y-6">
      <PlatformPageHeader
        title="Teams"
        description="Teams mit Abteilung, Leitung und Mitgliedern."
        action={
          canWrite ? (
            <Button type="button" onClick={() => setDialog({ mode: 'create' })}>
              <PlusIcon className="size-4" aria-hidden />
              Team hinzufügen
            </Button>
          ) : undefined
        }
      />

      <div className={`${adminPanelClassName} max-w-sm`}>
        <Input
          value={searchInput}
          onChange={(event) => setSearchInput(event.target.value)}
          placeholder="Name oder Kurzname suchen …"
          aria-label="Teams suchen"
        />
      </div>

      {teams.isPending && (
        <p className="flex items-center gap-2 text-muted-foreground">
          <Loader2Icon className="size-4 animate-spin" aria-hidden />
          Teams werden geladen …
        </p>
      )}

      {teams.isError && (
        <p role="alert" className="text-sm text-destructive">
          {teams.error instanceof ApiRequestError
            ? teams.error.message
            : 'Die Teams konnten nicht geladen werden.'}
        </p>
      )}

      {teams.data && (
        <div className={adminTableFrameClassName}>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Abteilung</TableHead>
                <TableHead>Leitung</TableHead>
                <TableHead className="text-right">Mitglieder</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="w-12">
                  <span className="sr-only">Aktionen</span>
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {teams.data.items.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center text-muted-foreground">
                    Keine Teams gefunden.
                  </TableCell>
                </TableRow>
              ) : (
                teams.data.items.map((item) => (
                  <TableRow key={item.id} className={cn(!item.isActive && 'opacity-70')}>
                    <TableCell>
                      <div className="font-medium">{item.name}</div>
                      <div className="font-mono text-xs text-muted-foreground">{item.slug}</div>
                    </TableCell>
                    <TableCell>{item.department?.name ?? '—'}</TableCell>
                    <TableCell>{item.lead?.displayName ?? '—'}</TableCell>
                    <TableCell className="text-right tabular-nums">
                      <Button
                        type="button"
                        variant="link"
                        className="h-auto p-0 font-normal tabular-nums"
                        onClick={() => setMembers({ id: item.id, name: item.name })}
                      >
                        {item.userCount}
                      </Button>
                    </TableCell>
                    <TableCell>
                      {item.isActive ? (
                        <Badge variant="outline">Aktiv</Badge>
                      ) : (
                        <Badge variant="destructive">Deaktiviert</Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon-sm"
                            aria-label={`${item.name}: Aktionen`}
                          >
                            <MoreHorizontalIcon className="size-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem
                            onSelect={() => setMembers({ id: item.id, name: item.name })}
                          >
                            Mitglieder
                          </DropdownMenuItem>
                          {canWrite && (
                            <DropdownMenuItem
                              onSelect={() =>
                                setDialog({
                                  mode: 'edit',
                                  values: toFormValues(item),
                                  leadLabel: item.lead?.displayName,
                                })
                              }
                            >
                              Bearbeiten
                            </DropdownMenuItem>
                          )}
                          {canWrite &&
                            (item.isActive ? (
                              <DropdownMenuItem
                                variant="destructive"
                                onSelect={() =>
                                  setDeactivate({
                                    id: item.id,
                                    name: item.name,
                                    userCount: item.userCount,
                                    queueCount: item.queueCount,
                                  })
                                }
                              >
                                Deaktivieren
                              </DropdownMenuItem>
                            ) : (
                              <>
                                <DropdownMenuItem onSelect={() => void reactivate(item)}>
                                  Reaktivieren
                                </DropdownMenuItem>
                                <DropdownMenuItem
                                  variant="destructive"
                                  disabled={!item.canDelete}
                                  title={
                                    item.canDelete
                                      ? undefined
                                      : 'Löschen erst möglich, wenn keine Mitglieder oder Warteschlangen zugeordnet sind.'
                                  }
                                  onSelect={() =>
                                    setPendingDelete({ id: item.id, name: item.name })
                                  }
                                >
                                  Endgültig löschen
                                </DropdownMenuItem>
                              </>
                            ))}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      )}

      <TeamFormDialog
        open={dialog !== null}
        mode={dialog?.mode ?? 'create'}
        initial={dialog?.mode === 'edit' ? dialog.values : null}
        busy={busy}
        departments={(departments.data?.items ?? []).map((item) => ({
          id: item.id,
          name: item.name,
          isActive: item.isActive,
        }))}
        leads={leadOptions}
        onOpenChange={(open) => {
          if (!open) {
            setDialog(null);
          }
        }}
        onSubmit={handleFormSubmit}
      />

      <TeamMembersDialog
        open={members !== null}
        teamId={members?.id ?? null}
        teamName={members?.name ?? ''}
        canWrite={canWrite}
        agents={staff.data?.items ?? []}
        onOpenChange={(open) => {
          if (!open) {
            setMembers(null);
          }
        }}
      />

      <AlertDialog
        open={deactivate !== null}
        onOpenChange={(open) => {
          if (!open) {
            setDeactivate(null);
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Team deaktivieren?</AlertDialogTitle>
            <AlertDialogDescription>{usageHint(deactivate)}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={updateTeam.isPending}>Abbrechen</AlertDialogCancel>
            <AlertDialogAction
              disabled={updateTeam.isPending}
              onClick={(event) => {
                event.preventDefault();
                void confirmDeactivate();
              }}
            >
              {updateTeam.isPending ? (
                <>
                  <Loader2Icon className="size-4 animate-spin" aria-hidden />
                  Wird deaktiviert …
                </>
              ) : (
                'Deaktivieren'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog
        open={pendingDelete !== null}
        onOpenChange={(open) => {
          if (!open) {
            setPendingDelete(null);
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Team endgültig löschen?</AlertDialogTitle>
            <AlertDialogDescription>
              {pendingDelete ? `„${pendingDelete.name}“ wird unwiderruflich entfernt.` : null}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteTeam.isPending}>Abbrechen</AlertDialogCancel>
            <AlertDialogAction
              disabled={deleteTeam.isPending}
              onClick={(event) => {
                event.preventDefault();
                void confirmDelete();
              }}
            >
              {deleteTeam.isPending ? (
                <>
                  <Loader2Icon className="size-4 animate-spin" aria-hidden />
                  Wird gelöscht …
                </>
              ) : (
                'Endgültig löschen'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </section>
  );
}
